/**
 * OtherDeficienciesNoteSection — dopisek do rubryki CEEB „Inne uchybienia mające pośredni wpływ na działanie przewodów
 * kominowych", osobno dla każdego przeglądu budynku (zadanie 4 etap 1, Bronek 2026-10-21: przeglądy z papieru
 * wprowadzane w panelu; migr. 076 `inspections.other_deficiencies_note`).
 *
 * Writerem jest WYŁĄCZNIE panel (tablet tego pola nie zna — jego „Uwagi do zaleceń budynku" to inny tekst i inna
 * rubryka). Bot wpisuje dopisek pod akapitami kodów A (zdanie A4 o kartach mieszkań), przed liniami lokali.
 * Zapis z kontrolą wersji: `otherDeficienciesNoteBase` = treść widziana przy otwarciu edycji; 409
 * OTHER_DEFICIENCIES_NOTE_CHANGED → wczytujemy aktualną treść i mówimy to wprost (bez cichego nadpisania).
 */
import React, { useState } from 'react';
import { inspectionsApi, InspectionUpdateError } from '../services/api';
import { InputField } from '../ds/InputField';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';

/** Lustro OTHER_DEFICIENCIES_NOTE_MAX (backend utils/otherDeficienciesNote.js). */
export const OTHER_DEFICIENCIES_NOTE_MAX = 2000;

interface Props {
  inspectionId: string;
  /** Dopisek z ostatniego odczytu przeglądu ('' = brak). */
  value: string;
  /** Po zapisie albo wczytaniu aktualnej treści (konflikt) — nowa wartość do stanu widoku budynku. */
  onChange: (next: string) => void;
}

const boxStyle: React.CSSProperties = {
  marginTop: 'var(--space-4)',
  padding: 'var(--space-4)',
  background: 'var(--surface-fill)',
  borderRadius: 'var(--radius-md)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
};

export function OtherDeficienciesNoteSection({ inspectionId, value, onChange }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [base, setBase] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startEdit = () => {
    setDraft(value);
    setBase(value);
    setError(null);
    setEditing(true);
  };

  const tooLong = draft.trim().length > OTHER_DEFICIENCIES_NOTE_MAX;

  const save = async () => {
    if (tooLong) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await inspectionsApi.update(inspectionId, { otherDeficienciesNote: draft, otherDeficienciesNoteBase: base });
      onChange(updated.otherDeficienciesNote ?? draft.trim());
      setEditing(false);
    } catch (e) {
      const err = e as InspectionUpdateError;
      if (err.status === 409 && err.code === 'OTHER_DEFICIENCIES_NOTE_CHANGED') {
        try {
          const fresh = await inspectionsApi.getById(inspectionId);
          const current = fresh.otherDeficienciesNote || '';
          onChange(current);
          setBase(current);
          setError('Dopisek zmieniono w międzyczasie w innym oknie — wyżej jest aktualna treść. Twoja wersja jest w polu: popraw ją i zapisz ponownie.');
        } catch {
          setError('Dopisek zmieniono w międzyczasie w innym oknie, a aktualnej treści nie udało się wczytać. Odśwież widok budynku.');
        }
      } else {
        setError(err.message || 'Nie udało się zapisać dopisku. Sprawdź połączenie z serwerem.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-label="Dopisek do „Inne uchybienia…”" style={boxStyle} data-testid="other-deficiencies-note">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', color: 'var(--text-primary)' }}>
          Dopisek do „Inne uchybienia…” (protokół CEEB):
        </div>
        {!editing && (
          <Button variant="secondary" icon="pencil" onClick={startEdit}>
            {value ? 'Edytuj dopisek' : 'Dodaj dopisek'}
          </Button>
        )}
      </div>

      {value ? (
        <div style={{ fontSize: 'var(--text-body)', whiteSpace: 'pre-wrap', color: 'var(--text-primary)' }}>{value}</div>
      ) : (
        !editing && <div style={{ fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>Brak dopisku.</div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-deny-bg)',
            color: 'var(--status-deny)',
            fontSize: 'var(--text-label)',
          }}
        >
          <Icon name="triangle-alert" size={18} />
          <span>{error}</span>
        </div>
      )}

      {editing && (
        <>
          <InputField
            label="Treść dopisku"
            multiline
            rows={4}
            value={draft}
            disabled={saving}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={'np. Należy wymienić uszkodzone nasady typu "h" w pionie 1 oraz 3 (3 sztuki)'}
            errorMessage={tooLong ? `Za długi dopisek (${draft.trim().length} znaków, limit ${OTHER_DEFICIENCIES_NOTE_MAX}).` : undefined}
            hint="Puste pole i zapis = usunięcie dopisku w panelu. Jeśli dopisek był już w CEEB, bot nadpisze rubrykę przy kolejnej wysyłce tylko wtedy, gdy ma do niej inną treść (np. tekst o kartach mieszkań) — inaczej usuń go w CEEB ręcznie."
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <Button variant="secondary" onClick={() => { setEditing(false); setError(null); }} disabled={saving}>Anuluj</Button>
            <Button variant="primary" icon="check" onClick={save} disabled={saving || tooLong}>
              {saving ? 'Zapisywanie…' : 'Zapisz dopisek'}
            </Button>
          </div>
        </>
      )}

      <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
        Wpisywane tylko w panelu. Bot wpisze go w protokole CEEB do rubryki „Inne uchybienia mające pośredni wpływ na
        działanie przewodów kominowych” — pod tekstem o kartach mieszkań. Zmiana po wysyłce trafi do CEEB dopiero przy
        kolejnym „Wyślij do CEEB”.
      </div>
    </section>
  );
}
